import { useEffect } from 'react';
import type { onMediaSrcExpiryErrorPropsT } from '../../../../../common/types/types';
import type { MEDIA_TYPE } from '../constants/media';
import { REFRESH_BUFFER_MS } from '../constants/media';
import { DOCUMENT_EVENT_LISTENER, MEDIA_EVENT_LISTENER, WINDOW_EVENT_LISTENER } from '../constants/video-events';
import { getNetworkMediaError } from '../utils/getNetworkMediaError';
import { useShowPreRefreshSnackbar } from './use-show-pre-refresh-snackbar';
type PropsT = {
  type: MEDIA_TYPE;
  srcExpiryTime: number | null;
  mediaElement: HTMLVideoElement | HTMLAudioElement | null;
  onSignedUrlExpired: (props: onMediaSrcExpiryErrorPropsT) => void;
  isSignedUrlRefreshingSupported: boolean;
};

// Browsers will retry expired signed URLs multiple times (often 10–15 range requests)
// before surfacing a 'video.error'. This happens because the media pipeline issues
// Range GETs under no-CORS rules: each 400 response is opaque to JavaScript, and
// ORB (Opaque Response Blocking) prevents the page from observing them. The player
// keeps retrying until it fully gives up, then finally dispatches the error event.
// To avoid wasted retries and long delays, we proactively refresh the signed URL
// (using expiry params) or just-in-time (on play/visibility) so the <video> always
// has a valid source.
export function useRefreshSignedUrlBeforeExpiryTimeout({
  srcExpiryTime,
  mediaElement,
  onSignedUrlExpired,
  isSignedUrlRefreshingSupported
}: PropsT) {
  const showPreRefreshSnackbar = useShowPreRefreshSnackbar();

  // Preemptive refresh: prevents the browser from hammering an expired URL with
  // repeated failed range requests before 'error' eventually fires.
  useEffect(() => {
    if (!srcExpiryTime) {
      return;
    }
    if (!isSignedUrlRefreshingSupported) {
      return;
    }
    const timeoutDuration = Math.max(0, srcExpiryTime - Date.now() - REFRESH_BUFFER_MS);
    const timeout = setTimeout(async () => {
      await showPreRefreshSnackbar();
      const mediaError = getNetworkMediaError();
      onSignedUrlExpired({
        mediaError,
        skipSourceValidation: true
      });
    }, timeoutDuration);
    return () => clearTimeout(timeout);
  }, [srcExpiryTime, onSignedUrlExpired, showPreRefreshSnackbar, isSignedUrlRefreshingSupported]);

  // Guard on user actions/visibility: timers are throttled in background tabs,
  // so a scheduled refresh may never run. We check again on 'play' and when the
  // tab becomes visible to avoid returning with an expired URL.
  useEffect(() => {
    if (!mediaElement) {
      return;
    }
    if (!isSignedUrlRefreshingSupported) {
      return;
    }
    const maybeRefreshSignedUrl = async () => {
      if (!srcExpiryTime) {
        return;
      }
      const isOverExpiryBufferWindow = Date.now() > srcExpiryTime - REFRESH_BUFFER_MS;
      if (isOverExpiryBufferWindow) {
        await showPreRefreshSnackbar();
        const mediaError = getNetworkMediaError();
        onSignedUrlExpired({
          mediaError,
          skipSourceValidation: true
        });
      }
    };
    const handlePlay = async () => {
      maybeRefreshSignedUrl();
    };
    const handleDocumentVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      maybeRefreshSignedUrl();
    };
    mediaElement.addEventListener(MEDIA_EVENT_LISTENER.PLAY, handlePlay);
    document.addEventListener(DOCUMENT_EVENT_LISTENER.VISIBILITY_CHANGE, handleDocumentVisibilityChange);
    window.addEventListener(WINDOW_EVENT_LISTENER.PAGE_SHOW, handleDocumentVisibilityChange);
    return () => {
      mediaElement.removeEventListener(MEDIA_EVENT_LISTENER.PLAY, handlePlay);
      document.removeEventListener(DOCUMENT_EVENT_LISTENER.VISIBILITY_CHANGE, handleDocumentVisibilityChange);
      window.removeEventListener(WINDOW_EVENT_LISTENER.PAGE_SHOW, handleDocumentVisibilityChange);
    };
  }, [mediaElement, onSignedUrlExpired, srcExpiryTime, showPreRefreshSnackbar, isSignedUrlRefreshingSupported]);
}